import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'shared/components/UC/Button';
import BackLink from '../components/BackLink';
import CriteriaScoresSection from '../components/evaluation/CriteriaScoresSection';
import EvaluationDetailsGrid from '../components/evaluation/EvaluationDetailsGrid';
import applicationService, {
  ApplicationServiceError,
} from '../services/applicationService';
import { ApplicationWithEvaluations } from '../types';
import styles from './ApplicationEvaluations.module.css';

const ApplicationEvaluations: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const [applicationData, setApplicationData] =
    useState<ApplicationWithEvaluations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEvaluations, setExpandedEvaluations] = useState<Set<string>>(
    new Set()
  );

  const loadApplicationData = useCallback(async () => {
    if (!applicationId) return;
    try {
      setLoading(true);
      setError(null);
      const data =
        await applicationService.getApplicationEvaluations(applicationId);
      setApplicationData(data);
    } catch (err) {
      const errorMessage =
        err instanceof ApplicationServiceError
          ? err.message
          : 'Error al cargar las evaluaciones';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (applicationId) {
      loadApplicationData();
    } else {
      setError('ID de aplicación no proporcionado');
      setLoading(false);
    }
  }, [applicationId, loadApplicationData]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const calculateFinalScore = (criteriaEvaluations: Record<string, any>) => {
    const scores = Object.values(criteriaEvaluations);
    if (scores.length === 0) return 0;
    const total = scores.reduce(
      (sum: number, c: any) => sum + (c.score ?? 1),
      0
    );
    const maxTotal = scores.reduce(
      (sum: number, c: any) => sum + c.maxScore,
      0
    );
    return (total / maxTotal) * 5;
  };

  const toggleEvaluation = (evaluationId: string) => {
    setExpandedEvaluations((prev) => {
      const next = new Set(prev);
      if (next.has(evaluationId)) next.delete(evaluationId);
      else next.add(evaluationId);
      return next;
    });
  };

  const handleBack = () => navigate('/researcher/dashboard');

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <h2>Cargando evaluaciones...</h2>
          <p>Por favor espere.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Error al cargar las evaluaciones</h2>
          <p>{error}</p>
          <div className={styles.errorActions}>
            <Button variant="primary" onClick={loadApplicationData}>
              Reintentar
            </Button>
            <Button variant="secondary" onClick={handleBack}>
              Volver
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!applicationData) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Aplicación no encontrada</h2>
          <Button variant="primary" onClick={handleBack}>
            Volver
          </Button>
        </div>
      </div>
    );
  }

  const projectTitle =
    applicationData.answers?.sectionAnswers?.PROJECT_INFORMATION
      ?.projectTitle ||
    applicationData.answers?.sectionAnswers?.APPLICATION_INFORMATION
      ?.projectTitle ||
    'Sin título';

  const avgScore =
    applicationData.evaluationSummary?.weightedScoreDistribution?.average;

  return (
    <div className={styles.container}>
      {/* Page header */}
      <div className={styles.pageHeader}>
        <BackLink to="mis postulaciones" onClick={handleBack} />
        <div className={styles.pageTitle}>
          <h1>Evaluaciones de la Postulación</h1>
          <div className={styles.folioLine}>
            <span className={styles.folioChip}>
              {applicationData.applicationNumber}
            </span>
            <span className={styles.projectTitle}>{projectTitle}</span>
          </div>
        </div>
      </div>

      {/* Info bar */}
      <div className={styles.infoBar}>
        <div className={styles.infoBarItem}>
          <span className={styles.infoBarLabel}>Concurso</span>
          <span className={styles.infoBarValue}>
            {applicationData.formTemplate.name}
          </span>
        </div>
        <div className={styles.infoBarDivider} />
        <div className={styles.infoBarItem}>
          <span className={styles.infoBarLabel}>Fecha de envío</span>
          <span className={styles.infoBarValue}>
            {formatDate(applicationData.submittedAt)}
          </span>
        </div>
        <div className={styles.infoBarDivider} />
        <div className={styles.infoBarItem}>
          <span className={styles.infoBarLabel}>Evaluaciones</span>
          <span className={styles.infoBarValue}>
            {applicationData.evaluations.length}
          </span>
        </div>
        {avgScore != null && (
          <>
            <div className={styles.infoBarDivider} />
            <div className={styles.infoBarItem}>
              <span className={styles.infoBarLabel}>Puntaje final</span>
              <span className={`${styles.infoBarValue} ${styles.infoBarScore}`}>
                {avgScore.toFixed(2)}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Evaluations */}
      <div className={styles.evaluationsSection}>
        <h2>Evaluaciones ({applicationData.evaluations.length})</h2>
        <div className={styles.evaluationsList}>
          {applicationData.evaluations.map((evaluation, index) => {
            const isExpanded = expandedEvaluations.has(evaluation.id);
            const score =
              evaluation?.finalScore ??
              calculateFinalScore(evaluation.criteriaEvaluations);
            return (
              <div key={evaluation.id} className={styles.evaluationCard}>
                <div
                  className={styles.evaluationHeader}
                  onClick={() => toggleEvaluation(evaluation.id)}
                >
                  <div className={styles.evaluationHeaderLeft}>
                    <span className={styles.evalNumber}>
                      Evaluación #{index + 1}
                    </span>
                    {evaluation.submittedAt && (
                      <span className={styles.evalDate}>
                        Enviada el {formatDate(evaluation.submittedAt)}
                      </span>
                    )}
                  </div>
                  <div className={styles.evaluationHeaderRight}>
                    <span className={styles.evalScore}>
                      <strong>{score.toFixed(2)}</strong>
                      <span className={styles.evalScoreMax}> / 5.00</span>
                    </span>
                    <span className={styles.expandIcon}>
                      <i className="uc-icon icon-size--sm">
                        {isExpanded ? 'expand_less' : 'expand_more'}
                      </i>
                    </span>
                  </div>
                </div>

                {isExpanded && (
                  <div className={styles.evaluationContent}>
                    <CriteriaScoresSection
                      criteriaEvaluations={evaluation.criteriaEvaluations}
                      showComments={true}
                    />
                    <EvaluationDetailsGrid
                      evaluation={evaluation}
                      showEvaluatorInfo={false}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ApplicationEvaluations;
